import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Box,
  Alert,
  Select,
  Option,
} from '@mui/joy';
import { CheckCircle } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import type { Board, BoardStatus } from '../../types';

interface WonConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (actualValue: number, notes: string, boardId: number, statusId: number, jobName: string) => void;
  opportunityName: string;
  estimatedValue: number;
}

export const WonConfirmModal: React.FC<WonConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  opportunityName,
  estimatedValue,
}) => {
  const [jobName, setJobName] = useState('');
  useEffect(() => {
    if (open) setJobName(opportunityName);
  }, [open, opportunityName]);

  const [actualValue, setActualValue] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [boards, setBoards] = useState<Board[]>([]);
  const [statuses, setStatuses] = useState<BoardStatus[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Update actualValue when modal opens with a new opportunity
  useEffect(() => {
    if (open && estimatedValue !== undefined && estimatedValue !== null) {
      setActualValue(estimatedValue.toString());
    }
  }, [open, estimatedValue]);

  // Load boards when modal opens
  useEffect(() => {
    if (open) {
      loadBoards();
    }
  }, [open]);

  // Load statuses when board is selected
  useEffect(() => {
    if (selectedBoardId) {
      loadStatuses(selectedBoardId);
    }
  }, [selectedBoardId]);

  const loadBoards = async () => {
    try {
      setLoading(true);
      const response = await api.getBoards();
      if (response.data) {
        // Handle both paginated and non-paginated responses
        const boardList = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).results || [];
        setBoards(boardList);
        // Auto-select first board if available
        if (boardList.length > 0) {
          setSelectedBoardId(boardList[0].id);
        }
      }
    } catch {
      // Intentionally empty
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async (boardId: number) => {
    try {
      const response = await api.getBoardStatuses(boardId);
      if (response.data) {
        setStatuses(response.data);
        // Auto-select first status if available
        if (response.data.length > 0) {
          setSelectedStatusId(response.data[0].id);
        }
      }
    } catch {
      // Intentionally empty
    }
  };

  const handleConfirm = () => {
    const value = parseFloat(actualValue);
    if (isNaN(value) || value < 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (!selectedBoardId || !selectedStatusId) {
      setError('Please select a board and status for the new job');
      return;
    }
    
    onConfirm(value, notes, selectedBoardId, selectedStatusId, jobName.trim());
    // Reset form
    setActualValue('');
    setNotes('');
    setError('');
    setSelectedBoardId(null);
    setSelectedStatusId(null);
  };

  const handleClose = () => {
    // Reset form
    setActualValue('');
    setNotes('');
    setError('');
    setSelectedBoardId(null);
    setSelectedStatusId(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        size="md"
        sx={{
          minWidth: { xs: 0, sm: 450 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: 600 },
          width: { xs: '100%', sm: 'auto' },
          maxHeight: 'calc(100dvh - 32px)',
          overflow: 'auto',
        }}
      >
        <ModalClose />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CheckCircle size={24} color="#4caf50" />
          <Typography level="h4">
            Mark Opportunity as Won
          </Typography>
        </Box>
        
        <Alert color="success" sx={{ mb: 3 }}>
          Congratulations! This will create a new job from the opportunity "{opportunityName}".
        </Alert>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Job Name</FormLabel>
          <Input
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            placeholder="Enter job name (optional)"
            slotProps={{ input: { maxLength: 255 } }}
          />
        </FormControl>

        <FormControl sx={{ mb: 2 }} error={!!error}>
          <FormLabel>Final Contract Value</FormLabel>
          <Input
            value={actualValue}
            onChange={(e) => {
              setActualValue(e.target.value);
              setError('');
            }}
            startDecorator="$"
            type="number"
            placeholder="Enter final amount"
            slotProps={{
              input: {
                step: '0.01'
              }
            }}
          />
          {error && (
            <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Job Board</FormLabel>
          <Select
            value={selectedBoardId}
            onChange={(_, value) => setSelectedBoardId(value as number)}
            placeholder="Select board for new job"
            disabled={loading || boards.length === 0}
          >
            {boards.map((board) => (
              <Option key={board.id} value={board.id}>
                {board.name}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Initial Status</FormLabel>
          <Select
            value={selectedStatusId}
            onChange={(_, value) => setSelectedStatusId(value as number)}
            placeholder="Select initial status"
            disabled={!selectedBoardId || statuses.length === 0}
          >
            {statuses.map((status) => (
              <Option key={status.id} value={status.id}>
                {status.name}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 3 }}>
          <FormLabel>Notes (optional)</FormLabel>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional details about winning this opportunity..."
            minRows={3}
          />
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={handleClose}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="success"
            onClick={handleConfirm}
            startDecorator={<CheckCircle size={18} />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Job
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};